"use client";

import { usePathname, useParams } from "next/navigation";
import Link from "next/link";
import { useAppDispatch, useAppSelector } from "appHooks";
import { fetchProjects } from "features/project/project.actions";
import { setLocale } from "features/config/config.reducer";
import { fetchClient } from "features/client/client.actions";
import { useState, useRef } from "react";
import { configSelector } from "features/config/config.selectors";
import { apiHeadersSelector } from "features/selectors";

interface Language {
  code: string;
  name: string;
}

const languages: Language[] = [
  { code: "en", name: "English" },
  { code: "ar", name: "عربي" },
];

export default function LanguageSwitcher({
  setIsLoading,
}: {
  setIsLoading: (val: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { slug } = useAppSelector(configSelector);
  const apiHeaders = useAppSelector(apiHeadersSelector);
  const { locale: currentLocale } = useAppSelector(configSelector);
  const pathname = usePathname();
  const params = useParams();
  // Use URL-derived locale for active state to avoid SSR/client hydration mismatch
  const urlLocale = (params?.locale as string) ?? currentLocale;
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState("");
  const isFetching = useRef(false); // Prevent multiple calls

  const fetchClientData = async (langCode?: string) => {
    if (isFetched || isFetching.current) return;
    isFetching.current = true; // Set flag to true before fetching
    try {
      await dispatch(
        fetchClient({ clientSlug: slug, langCode: langCode || "", apiHeaders })
      );
      setIsFetched(true);
    } catch (error: any) {
      setError(error.message);
    } finally {
      isFetching.current = false;
    }
  };

  // Utility function to generate the link URL
  const changeLocaleAndFetchClient = async (langCode: string) => {
    setIsLoading(true);
    dispatch(setLocale(currentLocale === "en" ? "ar" : "en"));
    try {
      await fetchClientData(langCode);
      await fetchProjects(apiHeaders);
    } catch (error: any) {
    } finally {
      const newPath = pathname.replace(/^\/(en|ar)(\/|$)/, `/${langCode}$2`);

      return (window.location.href = newPath);
    }
  };

  return (
    <div
      className="flex gap-4"
      style={{
        zIndex: 10000,
        display: "flex",
        justifyContent: "space-between",
        width: "100px",
      }}
    >
      {languages.map((lang) => (
        <Link
          href={""}
          key={lang.code}
          onClick={(e) => {
            e.preventDefault();
            changeLocaleAndFetchClient(lang.code);
          }}
          className={`${urlLocale === lang.code ? "font-bold" : ""} ${
            lang.code === "en" ? "app-font-en" : "app-font-ar"
          } lang`}
        >
          {lang.name}
        </Link>
      ))}
    </div>
  );
}
